import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Order, AlertData } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";
import { useClipsStore } from "./ClipsStore/clipsStore";
import { EnhancedFrontelTableHead } from "./EnhancedFrontelTableHead";
import { formatClipReceivedLocal } from "./ClipsUtilities/formatTimeStamp";
import { useNavigate, useSearchParams } from "react-router-dom";
import { handleButtonShadingFrontel } from "./ClipsUtilities/handleButtonShadingFrontel";
import {
  removeClipsIdParamsFromUrl,
} from "./ClipsUtilities/clipsUrlParams";

interface FrontelTableProps {
  order: Order;
  orderBy: string;
  setOrder: (order: Order) => void;
  setOrderBy: (orderBy: string) => void;
}

export const FrontelTable = ({
  order,
  orderBy,
  setOrder,
  setOrderBy,
}: FrontelTableProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const lastAutoPagedSelectionRef = React.useRef<string | null>(null);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    pageFrontel,
    setPageFrontel,
    frontelData,
    setFrontelData,
    setSelectedRowFrontel,
    setVideoClipLoadingFrontel,
    getClipInfoFrontel,
    getVideoURLFrontel,
    frontelSite,
    setVerifiedClickedFrontel,
    setUnverifiedClickedFrontel,
  } = useClipsStore();

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof AlertData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageFrontel(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageFrontel(0);
  };

  const handleRowClick = (row: any) => {
    // Update all rows to set selected to false
    const updatedData = frontelData.map((item: any) => ({
      ...item,
      selected: false,
    }));

    // Find and update the clicked row to set selected to true
    const updatedDataWithSelection = updatedData.map((item: any) => {
      if (item.file_id === row.file_id) {
        return { ...item, selected: true };
      }
      return item;
    });

    // Update the data in the store
    setFrontelData(updatedDataWithSelection);

    // Set the selected row and trigger loading
    setVideoClipLoadingFrontel(true);
    setSelectedRowFrontel(row);
    localStorage.setItem("clipsSelectedRow", JSON.stringify(row));

    // Update verification button shading based on row's ground_truth
    handleButtonShadingFrontel(
      row,
      setVerifiedClickedFrontel,
      setUnverifiedClickedFrontel
    );

    // Fetch the full clip details (internal_id etc.) and the video URL for
    // the selected row
    getClipInfoFrontel(row.file_id, navigate);
    getVideoURLFrontel(row.file_id, navigate);
    removeClipsIdParamsFromUrl(setSearchParams, searchParams);
  };

  const roundToHundredth = (number: number) => {
    const newNumber = number * 100;
    return Math.round(newNumber * 100) / 100;
  };

  const emptyRows =
    pageFrontel > 0
      ? Math.max(0, (1 + pageFrontel) * rowsPerPage - frontelData.length)
      : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(frontelData, getComparator(order, orderBy)).slice(
        pageFrontel * rowsPerPage,
        pageFrontel * rowsPerPage + rowsPerPage
      ),
    [frontelData, order, orderBy, pageFrontel, rowsPerPage]
  );

  React.useEffect(() => {
    const sortedRows = stableSort(frontelData, getComparator(order, orderBy));
    const selectedIndex = sortedRows.findIndex((row: any) => row.selected);
    const selectedFileId: string | null =
      selectedIndex >= 0 && sortedRows[selectedIndex]?.file_id != null
        ? String(sortedRows[selectedIndex].file_id)
        : null;
    const expectedPage = selectedIndex >= 0 ? Math.floor(selectedIndex / rowsPerPage) : 0;

    if (
      selectedFileId &&
      selectedFileId !== lastAutoPagedSelectionRef.current &&
      expectedPage !== pageFrontel
    ) {
      lastAutoPagedSelectionRef.current = selectedFileId;
      setPageFrontel(expectedPage);
    }
  }, [frontelData, order, orderBy, pageFrontel, rowsPerPage]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 490 }}>
        <Table
          sx={{ fontFamily: "Mulish", postion: "absolute" }}
          aria-labelledby="frontel-clips-table"
          stickyHeader
        >
          <EnhancedFrontelTableHead
            onRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
          />
          <TableBody>
            {visibleRows.map((row: any) => {
              let sx: any = { cursor: "pointer" };
              if (row.selected) {
                sx = {
                  cursor: "pointer",
                  backgroundColor: "rgba(2, 136, 209, 0.2)",
                };
              }
              return (
                <TableRow
                  hover
                  onClick={() => handleRowClick(row)}
                  tabIndex={-1}
                  key={row.file_id}
                  sx={sx}
                >
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.customer_name || frontelSite.site_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.camera_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {formatClipReceivedLocal(
                      row.clip_header_received,
                      row.clip_received_local,
                      row.site_timezone
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "20%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.clip_result}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "15%",
                    }}
                  >
                    {row.probability != null
                      ? `${roundToHundredth(row.probability)}%`
                      : ""}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontSize: "10px",
                      fontFamily: "mulish",
                      width: "15%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {row.file_id}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={frontelData.length}
        rowsPerPage={rowsPerPage}
        page={pageFrontel}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
